import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {List} from 'immutable';
import {history} from 'client/history';
import {getModelForResourceType} from 'resources/modelRegistry';

import {Button, Heading, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import sessionStore from 'client/Session/SessionStore';

import PersonalSubscriptionCard from './PersonalSubscriptionCard';
import InstructionalLicenseCard from './InstructionalLicenseCard';

import './subscriptions.scss';

const getAllSubscriptionsQuery = () => {
  return resource('subscriptions_v1')
    .mandarkEndpoint(['subscriptions_v1'])
    .filter({
      user_v1: sessionStore.getUserId()
    })
    .include('subject_v2')
    .customQuery({
      sort: '-end_date'
    });
};

const getActiveSubscriptionsQuery = () => {
  return getAllSubscriptionsQuery()
    .filter({
      end_date: {
        greater_than_inclusive: moment().startOf('day').valueOf()
      }
    })
    .withMeta('subject_v2');
};

const getExpiredSubscriptionsQuery = () => {
  return getAllSubscriptionsQuery().filter({
    end_date: {
      less_than: moment().startOf('day').valueOf()
    }
  });
};

const Subscriptions = ({setIsReady, user}) => {
  const [showExpiredSubscriptions, setShowExpiredSubscriptions] = useState(false);
  const [activeSubscriptions, setActiveSubscriptions] = useState(List());
  const [expiredSubscriptions, setExpiredSubscriptions] = useState(List());

  const isStudent =
    !user.isTeacher() &&
    !user.isSchoolAdministrator() &&
    !user.isAuthor() &&
    !user.isPlatformAdministrator() &&
    !user.isPointOfContact();

  useEffect(() => {
    let subscribed = true;
    const fetchActiveSubscriptions = async () => {
      try {
        const res = await getActiveSubscriptionsQuery().getResourcePromise();
        if (subscribed) {
          setActiveSubscriptions(res);
        }
      } catch (error) {
        history.pushState(null, '/error');
      }
    };
    fetchActiveSubscriptions();
    return () => {
      subscribed = false;
    };
  }, []);

  useEffect(() => {
    let subscribed = true;
    const fetchExpiredSubscriptions = async () => {
      try {
        const res = await getExpiredSubscriptionsQuery().getResourcePromise();
        if (subscribed) {
          setExpiredSubscriptions(res);
        }
      } catch (error) {
        history.pushState(null, '/error');
      }
    };
    fetchExpiredSubscriptions();
    return () => {
      subscribed = false;
    };
  }, []);

  return (
    <>
      {!isStudent ? (
        <>
          <Heading size='xxs' withMargin className='subscription-settings__header'>
            Instructional licenses
          </Heading>

          <InstructionalLicenseCard setIsReady={setIsReady} user={user} />
        </>
      ) : null}

      {activeSubscriptions.size || expiredSubscriptions.size || isStudent ? (
        <Heading size='xxs' withMargin className='subscription-settings__header u-mar-t_7'>
          Personal subscriptions
        </Heading>
      ) : null}

      {activeSubscriptions.map((subscription) => (
        <PersonalSubscriptionCard key={subscription.getId()} subscription={subscription} />
      ))}

      {showExpiredSubscriptions
        ? expiredSubscriptions.map((subscription) => (
            <PersonalSubscriptionCard key={subscription.getId()} subscription={subscription} />
          ))
        : null}

      {expiredSubscriptions.size && !showExpiredSubscriptions ? (
        <Button
          border
          color='secondary'
          variant='text'
          className='show-expired-subscriptions-button u-mar-t_3 u-mar-b_4'
          onClick={() => setShowExpiredSubscriptions(true)}
        >
          Show expired personal subscriptions
        </Button>
      ) : null}

      {!activeSubscriptions.size && !expiredSubscriptions.size && isStudent ? (
        <Text>You have no personal subscriptions.</Text>
      ) : null}
    </>
  );
};

Subscriptions.propTypes = {
  user: PropTypes.instanceOf(getModelForResourceType('user_v2')),
  setIsReady: PropTypes.func
};

export default Subscriptions;
